import { useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import PolicyCTA from "@/components/policy/PolicyCTA";
import PolicyHero from "@/components/policy/PolicyHero";
import RevealStyles from "@/components/policy/RevealStyles";
import {
  Droplets,
  Zap,
  Users,
  DollarSign,
  MapPin,
  Shield,
  CheckCircle,
  XCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Building2,
  Home,
  Heart,
  Briefcase,
  Eye,
  Scale,
  AlertTriangle,
  Factory,
  GraduationCap,
  Leaf,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-white/[0.03]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors">
        <span className="font-bold text-white pr-4">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/30 flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5"><p className="text-white/50 leading-relaxed">{answer}</p></div>}
    </div>
  );
};

const faqs = [
  { question: "Is Cooper against AI or technology?", answer: "No. This is not an anti-technology position. This is an anti-subsidy, pro-taxpayer position. If a trillion-dollar tech corporation wants to build in Nevada, they are welcome to do so — on their own dime. What Nevada should not do is hand over tax breaks, scarce water, and public infrastructure to corporations that don't need public assistance." },
  { question: "Don't data centers bring jobs?", answer: "Data centers create temporary construction jobs during the build phase. However, once operational, most are highly automated facilities requiring relatively few permanent employees compared to the public resources they consume. Nevada should prioritize industries that create thousands of durable, long-term jobs for working families." },
  { question: "What about the economic benefits companies promise?", answer: "Promises of economic growth should be evaluated against real, measurable outcomes. When companies receive massive tax breaks, consume public water, strain the power grid, and create relatively few permanent jobs, the actual community benefit is often far less than what was promised. That's why this plan requires independent review and binding clawback provisions." },
  { question: "Would this make Nevada less competitive?", answer: "Nevada competes for investment by offering quality of life, affordable living, available land, and abundant sunshine. The state does not need to give away its water and tax revenue to attract business. Companies that require massive public subsidies to be profitable are not the kind of investment Nevada needs." },
  { question: "What should Nevada invest in instead?", answer: "Affordable housing, public clean energy, water infrastructure, healthcare, education, local small businesses, manufacturing, food security, and workforce training — investments that directly benefit Nevada families and create broad-based economic growth." },
];

const AIDataCentersPage = () => {
  useReveal();

  const nevadaFirstStandards = [
    "No massive tax abatements for trillion-dollar corporations",
    "Full public disclosure of water use",
    "Full public disclosure of electricity demand",
    "Binding local hiring requirements",
    "Binding permanent job creation requirements",
    "No ratepayer-funded utility upgrades for private profit",
    "No groundwater depletion",
    "No project approval without community input",
    "Independent environmental and water impact review",
    "Public benefit must outweigh public cost",
    "Clawbacks if promised jobs or benefits are not delivered",
  ];

  const comparisonRows = [
    { label: "Tax Breaks", corp: "Massive abatements for profitable corporations", nv: "No special treatment — fair taxes like everyone else" },
    { label: "Water Use", corp: "Industrial-scale consumption in the driest state", nv: "Water preserved for families, agriculture, and communities" },
    { label: "Energy Use", corp: "Enormous grid strain, potential rate increases", nv: "Clean energy that lowers bills for residents" },
    { label: "Permanent Jobs", corp: "Relatively few for highly automated facilities", nv: "Thousands of durable jobs in housing, energy, healthcare" },
    { label: "Public Infrastructure", corp: "Substations, roads, and utilities built for corporate use", nv: "Infrastructure serving schools, hospitals, and neighborhoods" },
    { label: "Community Benefit", corp: "Profits often leave the state", nv: "Investment stays in Nevada communities" },
    { label: "Accountability", corp: "Promises with no binding enforcement", nv: "Binding commitments with public clawback provisions" },
  ];

  return (
    <div data-testid="ai-data-centers-page" className="bg-[#0B1A3B]">
      {/* ── HERO ── */}
      <PolicyHero
        testId="ai-data-centers-hero"
        accentColor="#C87533"
        badge={{ icon: Shield, label: "Protecting Nevada's Resources" }}
        headline={
          <>
            Nevada's Water, Land, Energy, and Tax Dollars Should Serve{" "}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
              Nevadans
            </span>{" "}
            — Not AI Corporations
          </>
        }
      >
        <p className="text-lg sm:text-xl text-white/50 max-w-2xl mx-auto mb-10">
          AI data centers want massive public subsidies while consuming enormous water and electricity, using valuable land and infrastructure, and creating relatively few long-term jobs. Nevada should not sacrifice its future for corporate server farms.
        </p>

        <div className="flex flex-wrap justify-center gap-4">
          <a href="#the-plan" className="group inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]">
            Read the Plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="#nevada-first" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all backdrop-blur-sm">
            Protect Nevada Resources
          </a>
        </div>
      </PolicyHero>

      {/* ── ICON STATS BAR ── */}
      <section className="relative py-10">
        <div className="absolute inset-0 bg-[#D4AF37]/10 border-y border-[#D4AF37]/20" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal grid grid-cols-3 md:grid-cols-6 gap-6 text-center">
            {[
              { icon: Droplets, label: "Water" },
              { icon: Zap, label: "Energy" },
              { icon: Users, label: "Jobs" },
              { icon: DollarSign, label: "Taxes" },
              { icon: MapPin, label: "Land" },
              { icon: Eye, label: "Accountability" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="flex flex-col items-center gap-2">
                  <Icon className="w-6 h-6 text-[#D4AF37]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-white/60">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SECTION 1: THE PROBLEM ── */}
      <section id="the-plan" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Problem</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-6">
              Corporate AI Expansion Is Coming for Nevada
            </h2>
            <p className="text-white/50 leading-relaxed mb-4">
              Large tech and AI companies are targeting states like Nevada because they want cheap land, cheap power, tax breaks, and access to public infrastructure. These projects are often sold as "innovation" and "economic growth."
            </p>
            <p className="text-white/50 leading-relaxed">
              But the public cost can be enormous. Massive water consumption. Grid strain. Tax revenue lost to abatements. Valuable land consumed. Public infrastructure redirected to serve corporate needs. And at the end of it — relatively few permanent jobs for Nevadans.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: TAX BREAKS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]">
                <DollarSign className="w-6 h-6" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Massive Tax Breaks for Billion-Dollar Corporations
              </h2>
            </div>
            <p className="text-white/50 leading-relaxed mb-4">
              These companies often seek property tax abatements, sales tax exemptions, infrastructure subsidies, and utility advantages. Ordinary Nevadans pay their taxes. Small businesses pay their taxes. Homeowners pay their taxes.
            </p>
            <p className="text-white/50 leading-relaxed mb-8">
              Trillion-dollar tech corporations should not get special treatment that everyday Nevada families and businesses don't receive.
            </p>
            <div className="bg-white/[0.04] border-2 border-[#D4AF37]/30 p-8 text-center">
              <p className="text-white font-bold text-lg leading-relaxed">
                "If a project is profitable enough for Wall Street, it should not need taxpayer handouts from{" "}
                <span className="text-[#D4AF37]">Nevada families</span>."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: WATER ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-blue-500/20 text-blue-400">
                <Droplets className="w-6 h-6" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Nevada Cannot Afford Corporate Water Waste
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <p className="text-white/50 leading-relaxed">
                  Nevada is one of the driest states in the country. Residents are asked to conserve water every year. Farmers, families, and communities are already under pressure from limited supply.
                </p>
                <p className="text-white/50 leading-relaxed">
                  AI data centers require massive cooling systems and can consume industrial-scale water. Nevada's limited water should go to people, housing, agriculture, and future generations — not corporate server farms.
                </p>
              </div>
              <div className="space-y-3">
                {[
                  "Nevada is among the driest states in the U.S.",
                  "Residents face strict water conservation mandates",
                  "Lake Mead and Colorado River supply under pressure",
                  "Data centers require industrial cooling water",
                  "Every gallon used by a server farm is a gallon not available for families",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 bg-blue-500/[0.06] border border-blue-500/20 p-3">
                    <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/[0.04] border-2 border-blue-400/30 p-8 text-center">
              <p className="text-white font-bold text-lg leading-relaxed">
                "Nevadans should not be told to conserve while corporate server farms consume{" "}
                <span className="text-blue-400">industrial-scale water</span>."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: ENERGY ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-amber-500/20 text-amber-400">
                <Zap className="w-6 h-6" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                AI Data Centers Could Strain Nevada's Grid
              </h2>
            </div>
            <p className="text-white/50 leading-relaxed mb-4">
              AI data centers require enormous electricity — enough to power small cities. This can increase demand on the grid, require expensive transmission upgrades, and put pressure on ratepayers who end up footing the bill for infrastructure that serves private corporations.
            </p>
            <p className="text-white/50 leading-relaxed mb-8">
              Nevada should use its energy future to lower bills for residents, not power private AI empires. Cooper's broader energy plan builds public clean energy infrastructure that lowers costs for families and businesses first.
            </p>
            <Link to="/policies/clean-energy" className="inline-flex items-center gap-2 text-[#D4AF37] font-bold text-sm uppercase tracking-wider hover:text-[#F5D76E] transition-colors">
              Read the Clean Energy Plan <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: JOBS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-emerald-500/20 text-emerald-400">
                <Users className="w-6 h-6" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Few Permanent Jobs Compared to the Public Cost
              </h2>
            </div>
            <p className="text-white/50 leading-relaxed mb-6">
              Data centers create temporary construction jobs, but once operational, they are often highly automated facilities with relatively few permanent long-term positions. Nevada should prioritize industries that create thousands of durable jobs.
            </p>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {[
                { icon: Home, label: "Housing Construction" },
                { icon: Zap, label: "Renewable Energy" },
                { icon: Heart, label: "Healthcare" },
                { icon: Factory, label: "Manufacturing" },
                { icon: Building2, label: "Infrastructure" },
                { icon: Leaf, label: "Food Systems" },
                { icon: GraduationCap, label: "Education" },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-center gap-3 bg-emerald-500/[0.06] border border-emerald-500/20 p-3">
                    <Icon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-white/70 text-sm font-medium">{item.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="bg-white/[0.04] border-2 border-emerald-400/30 p-8 text-center">
              <p className="text-white font-bold text-lg">
                "Nevada should invest in{" "}
                <span className="text-emerald-400">jobs for people</span>{" "}
                — not warehouses for servers."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: LAND ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]">
                <MapPin className="w-6 h-6" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Land and Infrastructure Should Serve Communities
              </h2>
            </div>
            <p className="text-white/50 leading-relaxed mb-4">
              Data centers consume valuable land and require substations, roads, water systems, transmission lines, and other public infrastructure. This infrastructure is built and maintained with public dollars.
            </p>
            <p className="text-white/50 leading-relaxed">
              Public infrastructure should first serve housing, schools, small businesses, hospitals, and Nevada communities — not private corporate facilities whose profits leave the state.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 7: OVERSTATED BENEFITS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-6">
              The Benefits Are Often Overstated
            </h2>
            <p className="text-white/50 leading-relaxed mb-8">
              Companies promise innovation and economic growth, but local communities often receive far less than what was advertised.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Limited permanent employment after construction",
                "Heavy resource use — water, power, land",
                "Public subsidy costs borne by taxpayers",
                "Corporate profits leaving the state",
                "Infrastructure burdens on local communities",
                "Promised benefits with no binding accountability",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 bg-red-500/[0.06] border border-red-500/20 p-4">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white/60 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: NEVADA FIRST STANDARD ── */}
      <section id="nevada-first" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="text-center mb-12">
              <Shield className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Standard</p>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
                Cooper's Nevada First Development Standard
              </h2>
              <p className="text-white/40 max-w-2xl mx-auto">
                Cooper will oppose or reject AI data center projects unless they meet strict Nevada First standards.
              </p>
            </div>

            <div className="space-y-3 mb-10">
              {nevadaFirstStandards.map((item) => (
                <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-[#D4AF37]/20 p-4">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <Scale className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              The Comparison
            </h2>
          </div>

          <div className="reveal overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-4 text-white/30 text-xs uppercase tracking-wider border-b border-white/10" />
                  <th className="text-left p-4 text-red-400 text-xs uppercase tracking-wider border-b border-white/10 font-bold">Corporate AI Data Center Model</th>
                  <th className="text-left p-4 text-[#D4AF37] text-xs uppercase tracking-wider border-b border-white/10 font-bold">Nevada First Development</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label} className="border-b border-white/5">
                    <td className="p-4 text-white font-bold text-sm whitespace-nowrap">{row.label}</td>
                    <td className="p-4">
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-white/50 text-sm">{row.corp}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-white/70 text-sm">{row.nv}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── SECTION 9: WHAT NEVADA SHOULD INVEST IN ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
                What Nevada Should Invest In Instead
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Home, title: "Affordable Housing", desc: "Build homes Nevadans can actually afford" },
                { icon: Zap, title: "Lower Utility Bills", desc: "Public clean energy that lowers costs for families" },
                { icon: Droplets, title: "Water Infrastructure", desc: "Protect and expand Nevada's water future" },
                { icon: Heart, title: "Healthcare", desc: "Accessible, affordable care for working families" },
                { icon: GraduationCap, title: "Education", desc: "Invest in our children and our workforce" },
                { icon: Briefcase, title: "Small Business", desc: "Support local entrepreneurs, not mega-corporations" },
                { icon: Factory, title: "Manufacturing", desc: "Build things in Nevada that create real jobs" },
                { icon: Leaf, title: "Food Security", desc: "End food deserts and build local food systems" },
                { icon: Users, title: "Workforce Training", desc: "Prepare Nevadans for the jobs of the future" },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="bg-white/[0.04] border border-white/10 p-5">
                    <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                    <h3 className="text-white font-bold text-sm mb-1">{item.title}</h3>
                    <p className="text-white/40 text-xs">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Frequently Asked Questions</h2>
          </div>
          <div className="reveal space-y-3">
            {faqs.map((faq) => <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />)}
          </div>
        </div>
      </section>

      {/* ── CLOSING ── */}
      <PolicyCTA
        testId="ai-data-centers-cta"
        heading={
          <>
            Our Water, Land, Energy, and Tax Dollars Should Serve{" "}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
              Nevada Families First
            </span>
          </>
        }
        subtitle="I am not against technology. I am against forcing Nevada taxpayers to subsidize corporate extraction. Stand with Cooper for a Nevada that puts people before corporate giveaways."
        primary={{ label: "Join the Campaign", to: "/volunteer" }}
        secondary={{ label: "Read More Solutions", to: "/policies/more-coming-soon" }}
        next={{ label: "Protect Jobs From AI Automation", to: "/policies/automation" }}
      />

      <RevealStyles />
    </div>
  );
};

export default AIDataCentersPage;
